import Class from '@/Class';
import ComponentManager from '@/Management/ComponentManager';
import * as Convert from '@/Utility/Convert';
import Utilities from '@/Utility/Utilities';
import * as interfaces from '#/CoreFrameInterfaces';

/**
 * Repräsentiert die Oberklasse aller DataTransferObjects. Setzt Standard-Properties und bietet Funktionalität
 * zur erleichterten DataTransferObject-Entwicklung
 */
export default class DataTransferObject extends Class {

    /**
     * Der XML-Namespace
     */
    public xmlNamespace: string;

    /**
     * Beinhaltet die Namen der XML-Properties
     */
    public xmlProperties: Array<string>;

    constructor(params: interfaces.IDataTransferObjectConstructorParameter) {
        super();

        this.xmlNamespace = this.paramOrDefault(params.xmlNamespace, null);
        this.xmlProperties = this.paramOrDefault(params.xmlProperties, []);
    }

    /**
     * Setzt den Wert eines Properties dieser Instanz unter Beachtung des Typs
     */
    public setProperty(prop: string, value: string) {
        let propValue = this[prop];
        let isObservable = false;

        if (ko.isObservable(propValue)) {
            isObservable = true;
            propValue = propValue();
        }

        if (value && value['@i:nil'] === 'true') {
            propValue = null;
        } else if (_.isBoolean(propValue)) {
            propValue = JSON.parse(value);
        } else if (_.isNumber(propValue)) {
            propValue = JSON.parse(value);
        } else {
            propValue = value;
        }

        if (isObservable === true) {
            this[prop](propValue);
        } else {
            this[prop] = propValue;
        }
    }

    /**
     * Erzeugt aus der Instanz ein JS-Objekt, welches die XML-Properties beinhaltet
     */
    public getXMLObject() {
        const xmlObj: Record<string, any> = {};

        if (this.xmlNamespace != null) {
            xmlObj['@xmlns:i'] = 'http://www.w3.org/2001/XMLSchema-instance';
            xmlObj['@xmlns'] = 'http://schemas.datacontract.org/2004/07/' + this.xmlNamespace;
        }

        for (const xmlProp of this.xmlProperties) {
            const value = ko.isObservable(this[xmlProp]) ? this[xmlProp]() : this[xmlProp];

            if (Utilities.isNullOrUndefined(value)) {
                xmlObj[xmlProp] = null;
                continue;
            }

            if (!_.isArray(value)) {
                xmlObj[xmlProp] = (value instanceof DataTransferObject) ? value.getXMLObject() : ko.toJS(value);
                continue;
            }


            const arrayObj: Record<string, any[]> = {};
            for (const innerProp of value) {
                const innerValue = ko.isObservable(innerProp) ? innerProp() : innerProp;
                if (Utilities.isNullOrUndefined(innerValue))
                    continue;

                if (!(innerValue instanceof DataTransferObject)) {
                    throw "Array entries without DataTransferObject Type cannot be converted to XML";
                }

                const innerObject = innerValue.getXMLObject();
                const type = innerValue.type;

                if (Utilities.isNullOrUndefined(arrayObj[type])) {
                    arrayObj[type] = [];
                }

                arrayObj[type].push(innerObject[type]);
            }
            xmlObj[xmlProp] = arrayObj;
        }

        return { [this.type]: xmlObj };
    }

    /**
     * Setzt die Properties eines Objekts auf der Instanz eines DataTransferObjects
     */
    public static setProperties(instance: DataTransferObject, object: Object, convertToArray?: boolean) {
        for (let i in instance) {
            for (let j in object) {
                if (i === j) {
                    if (instance.xmlProperties.indexOf(i) === -1) {
                        instance.xmlProperties.push(i);
                    }

                    if (_.isArray(instance[i]) === false) {
                        instance.setProperty(i, object[j]);
                    } else {
                        if (object[j] != null) {

                            // Wenn zwar ein Array erwartet wird, aber nur ein Objekt vorhanden ist, dann stecke das Objekt
                            // in ein neues Array, falls das entsprechende Flag "convertToArray" gesetzt ist.
                            // Das Objekt wird somit als ein-elementiges Array interpretiert.
                            if (!_.isArray(object[j]) && convertToArray) {
                                object[j] = [object[j]];
                            }

                            if (_.isArray(object[j])) {
                                for (let k = 0; k < object[j].length; k++) {
                                    let value = object[j][k];

                                    if (_.isObject(value)) {
                                        value = DataTransferObject.createInstance(j, object[j][k], convertToArray);
                                    }

                                    instance[i].push(value);
                                }
                            } else {
                                let keys = _.keys(object[j]);
                                let alias = _.find(keys, (key: string) => {
                                    return key.substring(0, 1) !== '@';
                                });

                                if (object[j][alias] != null) {
                                    if (_.isArray(object[j][alias]) === false) {
                                        object[j][alias] = [object[j][alias]];
                                    }

                                    for (let k = 0; k < object[j][alias].length; k++) {
                                        let value = object[j][alias][k];

                                        if (_.isObject(value)) {
                                            value = DataTransferObject.createInstance(alias, object[j][alias][k], convertToArray);
                                        }

                                        instance[i].push(value);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Erzeugt eine neue DataTransferObject-Instanz anhand eines Alias
     */
    public static createInstance(alias: string, params: interfaces.IDataTransferObjectConstructorParameter, convertToArray?: boolean) {
        let Type = ComponentManager.getModel(alias) as any;

        if (Type == null) {
            return null;
        }

        let model = new Type();

        if (params != null) {
            DataTransferObject.setProperties(model, params, convertToArray);
        }

        if (model.init != null) {
            model.init();
        }

        return model;
    }

    /**
     * Erzeugt eine neue DataTransferObject-Instanz auf Basis eines JS-Objekts
     */
    public static parseFromObject(object: any, convertToArray: boolean): DataTransferObject {
        if (object == null) {
            return null;
        }

        let keys = _.keys(object);

        if (keys.length !== 1) {
            return null;
        }

        let alias = keys[0];
        let root = object[alias];
        let instance = DataTransferObject.createInstance(alias, root, convertToArray);

        return instance;
    }

    /**
     * Erzeugt eine neue DataTransferObject-Instanz auf Basis eines XML-Strings
     */
    public static parseFromXML(xml: string, convertToArray?: boolean): DataTransferObject {
        let object = Convert.XMLStringToObject(xml);
        let model = DataTransferObject.parseFromObject(object, convertToArray);

        return model;
    }

    /**
     * Erzeugt einen XML-String aus einer DataTransferObject-Instanz
     */
    public static toXMLString(instance: DataTransferObject) {
        let object = instance.getXMLObject();
        let xml = Convert.ObjectToXMLString(object);

        return xml;
    }
}